import React from 'react';
import PhoneCallbackOutlinedIcon from '@mui/icons-material/PhoneCallbackOutlined';
import PhoneForwardedOutlinedIcon from '@mui/icons-material/PhoneForwardedOutlined';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import ContactPhoneRoundedIcon from '@mui/icons-material/ContactPhoneRounded';
import { ACTIVITY_TYPES_ICONS } from './activityTypes';
import { PRACTICE_ICONS } from '../../Pages/PracticesPage/Utils/constants';
import { getForcedMessages } from '../../i18n';
import MarkChatReadRoundedIcon from '@mui/icons-material/MarkChatReadRounded';
import SpeakerNotesOffRoundedIcon from '@mui/icons-material/SpeakerNotesOffRounded';
import { Tooltip } from '@mui/material';
import ScheduleSendRoundedIcon from '@mui/icons-material/ScheduleSendRounded';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import CallEndOutlinedIcon from '@mui/icons-material/CallEndOutlined';
//scheduled means still ahead: a received SMS or a sent email carries a past scheduledOn and no completedOn
const isStillScheduled = (data) =>
  !data?.completedOn &&
  Boolean(data?.scheduledOn) &&
  new Date(data.scheduledOn) > new Date();
//for inbound calls without outcome, differentiate by the answered state
const incomingCallNoOutcomeKey = (answered) => {
  if (answered === true) return 'ANSWERED_INCOMING_CALL';
  if (answered === false) return 'NOT_ANSWERED_INCOMING_CALL';
  return 'INCOMING_CALL_NO_OUTCOME';
};

const renderCallIcon = (outbound, outcome, answered) => {
  if (!outbound)
    return (
      <PhoneCallbackOutlinedIcon sx={{ color: 'primary.main' }} size="large" />
    );
  if (outcome === 9)
    return <PhoneDisabledIcon sx={{ color: 'error.main' }} size="large" />;
  return !answered ? (
    <CallEndOutlinedIcon sx={{ color: 'error.main' }} size="large" />
  ) : (
    <PhoneForwardedOutlinedIcon sx={{ color: 'success.main' }} size="large" />
  );
};

const ACTIVITY_MAP = {
  SMS: (data, intl = {}, outcomes = []) => ({
    icon: data?.status ? (
      data?.status === 'DLVRD' ? (
        <Tooltip title={intl.messages.SMS_DELIVERED} placement="top" arrow>
          <MarkChatReadRoundedIcon
            sx={{ color: 'success.main', cursor: 'pointer' }}
            size="large"
          />
        </Tooltip>
      ) : (
        <Tooltip title={intl.messages.SMS_SEND_ERROR} placement="top" arrow>
          <SpeakerNotesOffRoundedIcon
            sx={{ color: 'error.main', cursor: 'pointer' }}
            size="large"
          />
        </Tooltip>
      )
    ) : !data?.performedOn ? (
      <ScheduleSendRoundedIcon size="large" />
    ) : (
      <ACTIVITY_TYPES_ICONS.SMS size="large" />
    ),
    primaryTxt: !data?.performedOn
      ? intl.messages.SMS_QUEUED
      : `${data?.activityType} (${
          data?.outbound ? intl.messages.OUTBOUND : intl.messages.INBOUND
        })`,
    from: data?.outbound ? intl.messages.BRANCH_NUMBER : data?.entityFullName,
    to: !data?.outbound ? intl.messages.BRANCH_NUMBER : data?.entityFullName,
    scheduledIcon: isStillScheduled(data) && (
      <ScheduleRoundedIcon sx={{ mr: '8px', fontSize: 'inherit' }} />
    ),
    dateTime: data?.completedOn || data?.scheduledOn || data?.createdOn,
    content: !data?.performedOn ? '' : data?.content,
    campaignAdvancement: outcomes?.find(({ id }) => id === data?.outcome)
      ?.campaignAdvancement,
    notPerformed: !data?.performedOn,
  }),
  CALL: (data, intl = {}, outcomes = []) => ({
    icon:
      !data?.completedOn && data?.scheduledOn ? (
        <ContactPhoneRoundedIcon
          sx={{ color: isStillScheduled(data) ? undefined : 'error.main' }}
        />
      ) : (
        renderCallIcon(data?.outbound, data?.outcome, data?.answered, outcomes)
      ),
    primaryTxt:
      intl.messages[
        outcomes?.find(({ id }) => id === data?.outcome)?.name?.toUpperCase() ??
          (data?.outbound
            ? 'NO_OUTCOME'
            : incomingCallNoOutcomeKey(data?.answered))
      ] ??
      outcomes?.find(({ id }) => id === data?.outcome)?.name?.toUpperCase(),
    from: !data?.outbound
      ? data?.entityFullName
      : data?.completedByFullName || data?.createdByFullName,
    to: !data?.outbound
      ? !(data?.completedByFullName ?? data?.createdByFullName)?.includes(
          'SYSTEM'
        )
        ? data?.completedByFullName || data?.createdByFullName
        : null
      : data?.entityFullName,
    scheduledIcon: isStillScheduled(data) && (
      <ScheduleRoundedIcon sx={{ mr: '8px', fontSize: 'inherit' }} />
    ),
    dateTime: data?.completedOn || data?.scheduledOn || data?.createdOn,
    content: data?.notes,
    campaignAdvancement: outcomes?.find(({ id }) => id === data?.outcome)
      ?.campaignAdvancement,
  }),
  EMAIL: (data, intl, outcomes = []) => ({
    icon: data?.sent ? (
      <Tooltip title={intl.messages.EMAIL_SENT} placement="top" arrow>
        <MarkEmailReadRoundedIcon sx={{ color: 'success.main' }} size="large" />
      </Tooltip>
    ) : (
      <ACTIVITY_TYPES_ICONS.EMAIL size="large" />
    ),
    primaryTxt: data?.subject,
    from: data?.from,
    to: data?.to,
    scheduledIcon: isStillScheduled(data) && (
      <ScheduleRoundedIcon sx={{ mr: '8px', fontSize: 'inherit' }} />
    ),
    dateTime: data?.completedOn || data?.scheduledOn || data?.createdOn,
    content: !data?.sent ? '' : data?.body,
    campaignAdvancement: outcomes?.find(({ id }) => id === data?.outcome)
      ?.campaignAdvancement,
    notPerformed: !data?.sent,
  }),
  APPOINTMENT: (data, intl = {}, outcomes = []) => ({
    icon: <ACTIVITY_TYPES_ICONS.APPOINTMENT size="large" />,
    primaryTxt:
      outcomes?.find(({ id }) => id === data?.outcome)?.name?.toUpperCase() ||
      intl.messages.APPOINTMENT_NOT_COMPLETED,
    from: data?.createdByFullName,
    to: data?.entityFullName || data?.baseEntityFullName,
    scheduledIcon: isStillScheduled(data) && (
      <ScheduleRoundedIcon sx={{ mr: '8px', fontSize: 'inherit' }} />
    ),
    dateTime: data?.completedOn || data?.scheduledOn || data?.createdOn,
    content: data?.notes,
    campaignAdvancement: outcomes?.find(({ id }) => id === data?.outcome)
      ?.campaignAdvancement,
  }),
  WHATSAPP: () => {},
  MANUAL: (data, intl) => ({
    icon: <ACTIVITY_TYPES_ICONS.MANUAL size="large" />,
    primaryTxt: intl.messages.MANUAL,
    from: data?.createdByFullName,
    content: data?.notes,
    dateTime: data?.createdOn,
    campaignAdvancement: data?.campaignAdvancement,
  }),
  DOSSIER: (data, intl, outcomes = []) => {
    const italianTerms = getForcedMessages('it');
    const outcomeName = Object.keys(italianTerms)?.find(
      (keyName) =>
        italianTerms[keyName]?.toUpperCase() ===
        outcomes?.find(({ id }) => id === data?.outcome)?.name?.toUpperCase()
    );
    const Icon =
      outcomeName && PRACTICE_ICONS[outcomeName]
        ? PRACTICE_ICONS[outcomeName]
        : ACTIVITY_TYPES_ICONS.DOSSIER;
    return {
      icon: <Icon size="large" />,
      primaryTxt: `${intl.messages.DOSSIER} - ${outcomes?.find(({ id }) => id === data?.outcome)?.name?.toUpperCase()}`,
      from: data?.completedByFullName || data?.createdByFullName,
      content: data?.notes,
      dateTime: data?.completedOn || data?.createdOn,
      campaignAdvancement: data?.campaignAdvancement,
    };
  },
};

export default ACTIVITY_MAP;
